import React, { useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Modal, Stack, TextField, Typography } from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { useActionHook, useNavigate } from "hooks";
import { IApiServiceError, useAreOnlyExternalAccountsSelect, useDealActions, useUserActions } from "store";
import { getDomain } from "utils/auth";

const apiDomain = getDomain();
const LOCALHOST = "localhost";

interface Props {
  id: number;
}

export const CopySharedDeal: React.FC<Props> = ({ id }) => {
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  const areOnlyExternalAccounts = useAreOnlyExternalAccountsSelect();
  const navigate = useNavigate();

  const { upgradeAccountAction } = useUserActions();
  const { copySharedDealAction } = useDealActions();

  const redirectToLogin = (domain: string) => {
    if (location.hostname === LOCALHOST) {
      return console.log(
        "If you hadn't been at localhost you would be redirected to log in to a newly created account."
      );
    }
    const link = document.createElement("a");
    link.href = `${import.meta.env.VITE_PROTOCOL}${domain}/main/deal/${id}`;
    link.click();
  };

  const { loading: upgradeLoading, action: upgradeAccount } = useActionHook(
    () => {
      if (upgradeLoading) return;
      if (!inputValue) return;
      return upgradeAccountAction({ domain: `${inputValue}${postfix}`, deal_id: id });
    },
    {
      onSuccess: () => {
        if (!token) return;
        redirectToLogin(`${inputValue}${postfix}`);
      },
      onReject: (error: IApiServiceError) => {
        const domainError = error.errors?.["domain"]?.[0];
        if (domainError) {
          setValidationError(domainError);
        }
      }
    }
  );

  const { action: copySharedDeal, loading: copyLoading } = useActionHook(copySharedDealAction, {
    onSuccess: (dealId) => {
      navigate({ route: "deal", params: { deal_id: dealId } });
    }
  });

  const loading = upgradeLoading || copyLoading;

  const postfix: string = useMemo(
    () =>
      "." +
      apiDomain
        .split(".")
        .filter((_: string, index: number, array: string[]) => index === array.length - 1 || index === array.length - 2)
        .join("."),
    []
  );

  const handleSubmit = useCallback(() => {
    if (areOnlyExternalAccounts) {
      return upgradeAccount();
    }
    copySharedDeal(id);
  }, [upgradeAccount, copySharedDeal, areOnlyExternalAccounts, id]);

  const handleOpenPopup = useCallback(() => {
    setIsPopupOpen(true);
  }, []);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
    setInputValue("");
    setValidationError("");
  }, []);

  return (
    <>
      <Button onClick={handleOpenPopup} size="sm" variant="contained">
        Copy deal
      </Button>
      <Modal open={isPopupOpen}>
        <ProgressOverlay
          containerSX={{
            ...style,
            display: "flex",
            flexDirection: "column",
            width: 520,
            p: "32px",
            borderRadius: "8px",
            bgcolor: "white",
            gap: "24px",
            boxShadow:
              "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
          }}
          inProgress={loading}
        >
          <Stack direction="row" sx={{ justifyContent: "space-between", borderBottom: "1px solid #DBE8F5", pb: "6px" }}>
            <Typography sx={{ fontSize: "18px", lineHeight: "24px", color: "#071F35", fontWeight: 400 }}>
              {areOnlyExternalAccounts ? "Upgrade account and copy deal" : "Copy deal"}
            </Typography>
            <IconButton size="sm" onClick={handleClosePopup}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {areOnlyExternalAccounts && (
            <Box sx={{ position: "relative" }}>
              <TextField
                label="Company Domain"
                placeholder="Domain"
                value={inputValue}
                onChange={(e) => {
                  const { value } = e.target;

                  // Limit with 25 symbols
                  if (value.length >= 35) return;

                  // Don't start with "-"
                  if (/^-/.test(value)) return;

                  // Let user enter only small letters and numbers
                  if (!/^[a-z,0-9,-]*$/.test(value)) return;

                  if (validationError) {
                    setValidationError("");
                  }

                  setInputValue(value);
                }}
                sx={{ width: "100%", zIndex: 1 }}
              />
              <TextField
                sx={{
                  width: "100%",
                  position: "absolute",
                  left: 0,
                  zIndex: 0
                }}
                disabled
                value={inputValue ? `${inputValue}${postfix}` : ""}
                label="Company Domain"
                helperText={validationError}
                error={Boolean(validationError)}
              />
            </Box>
          )}
          <Typography sx={{ color: "#071F35", fontSize: "14px", lineHeight: "16px", fontWeight: 400 }}>
            By pressing Apply you will copy the deal to your account. Going forward. you will not see any changes made
            to the deal by the person who shared the deal with you.
          </Typography>
          <Stack direction="row" sx={{ justifyContent: "flex-end", gap: "8px" }}>
            <Button onClick={handleClosePopup} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              {...(areOnlyExternalAccounts && { disabled: inputValue.length < 2 || Boolean(validationError) })}
            >
              Apply
            </Button>
          </Stack>
        </ProgressOverlay>
      </Modal>
    </>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px"
};
